import type { IScope } from "angular";
import { utc } from "moment-timezone";
import { finalize } from "rxjs/operators";
import "@progress/kendo-ui/js/kendo.colorpicker";

import { cloneDeep, isEmpty } from "lodash";

import { DefaultActions } from "App/Modules/Gui/Components/ActionMenu/DefaultActions";
import type { IAction } from "App/Modules/Gui/Components/ActionMenu/IAction";
import type { IConfigurationGroupDetection, IResponseWithLoading, ISite } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

const PALETTE = [
    "#EFE4B0",
    "#F8E71C",
    "#F5A623",
    "#FF530D",
    "#FF0000",
    "#880015",
    "#8302C1",
    "#08A179",
    "#5AE30B",
    "#417505",
    "#00D0FF",
    "#608BC2",
    "#323BC0",
    "#002065",
];

export default class ConfigurationGroupCareEditComponentController {
    public static $inject: string[] = ["$scope", "mmSitesRepository"];

    public original: IConfigurationGroupDetection;

    public modified: IConfigurationGroupDetection;

    public actions: IAction[];

    public site: IResponseWithLoading<ISite>;

    public editResponse: IResponseWithLoading<IConfigurationGroupDetection>;

    public deleteResponse: IResponseWithLoading<void>;

    public palette: string[] = PALETTE;

    public resolve: IFormContainerResolve<IConfigurationGroupDetection, void>;

    constructor(private readonly $scope: IScope, private readonly sitesRepository: ISitesRepository) {}

    public $onInit() {
        this.original = this.resolve.formData;
        this.modified = cloneDeep(this.original);

        this.actions = [
            {
                ...DefaultActions.DeleteAction,
                click: this.delete.bind(this),
                isDisabled: () =>
                    this.original.alarmZones.length > 0 || (this.editResponse && this.editResponse.isLoading),
                isTooltipHidden: () => this.original.alarmZones.length <= 0,
                response: () => this.deleteResponse,
                tooltip: () => "Gui.Manage.RemoveConfigurationGroupDetectionTooltip",
            },
        ];

        extendWithLoadingAndFail(this.sitesRepository.getSite(this.original.siteId))
            .pipe(
                finalize(() => {
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                (result) => {
                    this.site = result;
                },
                (result) => {
                    this.site = result;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public save() {
        const delta = PatchDelta.difference(this.original, this.modified);

        if (delta.schedule && delta.schedule.scheduleExceptions) {
            delta.schedule.scheduleExceptions = delta.schedule.scheduleExceptions.filter((exception) => {
                return exception.dtStop.isAfter(utc().tz(this.site.data.timeZone));
            });
        }

        if (!isEmpty(delta)) {
            extendWithLoadingAndFail(
                this.sitesRepository.patchConfigurationGroupDetection(this.original.siteId, this.original.id, delta),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IConfigurationGroupDetection>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.resolve.options.onCancel();
        }
    }

    public delete() {
        extendWithLoadingAndFail(this.sitesRepository.deleteConfigurationGroup(this.original.siteId, this.original.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;
                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
    }
}
